import { Link, useParams, useNavigate } from 'react-router-dom'
import { useMedicalRecordStore } from '@/stores/medicalRecordStore'

export default function MedicalRecordDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { getRecord, deleteRecord } = useMedicalRecordStore()
  
  const record = getRecord(id || '')

  const handleEdit = () => {
    navigate(`/medical-records/${id}/edit`)
  }

  const handlePrint = () => {
    window.print()
  }

  const handleDelete = () => {
    if (window.confirm('この診療記録を削除しますか？')) {
      deleteRecord(id || '')
      navigate('/medical-records')
    }
  }

  if (!record) {
    return (
      <>
        <header className="aip-main__header">
          <div className="aip-main__header-container">
            <Link to="/medical-records" className="aip-button aip-button--icon">
              <span className="material-symbols-outlined">arrow_back</span>
            </Link>
            <h1 className="aip-main__header-title">診療記録</h1>
          </div>
        </header>
        <div className="aip-main__content">
          <div className="error-container">
            <span className="material-symbols-outlined">error</span>
            <h2>診療記録が見つかりません</h2>
            <p>指定された診療記録は存在しないか、削除された可能性があります。</p>
            <Link to="/medical-records" className="aip-button aip-button--outline">
              一覧に戻る
            </Link>
          </div>
        </div>
        <style>{`
          .error-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: var(--spacing-xl);
            background-color: var(--background-primary);
            border-radius: var(--border-radius-lg);
            text-align: center;
          }
          .error-container .material-symbols-outlined {
            font-size: 64px;
            color: var(--color-alert);
            margin-bottom: var(--spacing-md);
          }
          .error-container h2 {
            margin-bottom: var(--spacing-sm);
          }
          .error-container p {
            color: var(--text-secondary);
            margin-bottom: var(--spacing-lg);
          }
        `}</style>
      </>
    )
  }

  return (
    <>
      <header className="aip-main__header no-print">
        <div className="aip-main__header-container">
          <Link to="/medical-records" className="aip-button aip-button--icon">
            <span className="material-symbols-outlined">arrow_back</span>
          </Link>
          <h1 className="aip-main__header-title">診療記録詳細</h1>
          <div className="aip-main__header-buttons">
            <button onClick={handlePrint} className="aip-button aip-button--outline">
              <span className="material-symbols-outlined">print</span>
              印刷
            </button>
            <button onClick={handleEdit} className="aip-button aip-button--outline">
              <span className="material-symbols-outlined">edit</span>
              編集
            </button>
            <button onClick={handleDelete} className="aip-button aip-button--outline aip-button--danger">
              <span className="material-symbols-outlined">delete</span>
              削除
            </button>
          </div>
        </div>
      </header>

      <div className="aip-main__content">
        {/* 印刷用ヘッダー */}
        <div className="print-header print-only">
          <h1>診療記録</h1>
          <p>FMT JAPAN 治療経過管理システム</p>
        </div>

        <div className="detail-container">
          {/* 基本情報 */}
          <section className="detail-section">
            <h2 className="detail-section__title">
              <span className="material-symbols-outlined">info</span>
              基本情報
            </h2>
            <div className="detail-grid">
              <div className="detail-item">
                <label>記録日</label>
                <span>{record.recordDate}</span>
              </div>
              <div className="detail-item">
                <label>記録種別</label>
                <span className={`record-type-badge record-type-badge--${record.recordType}`}>
                  {record.recordType === 'doctor' ? '医師記録' : '患者回答'}
                </span>
              </div>
              <div className="detail-item">
                <label>研究会ID</label>
                <Link to={`/patients/${record.patientRecordId}`} className="detail-link">
                  {record.researchId}
                </Link>
              </div>
              <div className="detail-item">
                <label>患者名</label>
                <span>{record.patientName}</span>
              </div>
              <div className="detail-item">
                <label>性別・年齢</label>
                <span>{record.gender} / {record.age}歳</span>
              </div>
              <div className="detail-item">
                <label>疾患名</label>
                <span className="disease-badge">{record.diseaseName}</span>
              </div>
              <div className="detail-item">
                <label>移植回数</label>
                <span>{record.transplantCount}回</span>
              </div>
            </div>
          </section>

          {/* 医療機関・担当医 */}
          <section className="detail-section">
            <h2 className="detail-section__title">
              <span className="material-symbols-outlined">local_hospital</span>
              医療機関・担当医
            </h2>
            <div className="detail-grid">
              <div className="detail-item detail-item--full">
                <label>医療機関</label>
                <Link to={`/master/hospitals/${record.hospitalId}`} className="detail-link">
                  {record.hospitalName}
                </Link>
              </div>
              <div className="detail-item">
                <label>担当医師</label>
                <div className="doctor-info">
                  <span className="doctor-avatar">{record.doctorName.charAt(0)}</span>
                  <span>{record.doctorName}</span>
                </div>
              </div>
            </div>
          </section>

          {/* SOAP記録 */}
          <section className="detail-section">
            <h2 className="detail-section__title">
              <span className="material-symbols-outlined">edit_note</span>
              診療記録 (SOAP)
            </h2>
            
            <div className="soap-container">
              <div className="soap-item soap-item--s">
                <div className="soap-header">
                  <span className="soap-badge">S</span>
                  <span className="soap-title">主観的情報 (Subjective)</span>
                </div>
                <div className="soap-content">
                  {record.subjectiveNote || <span className="no-data">記録なし</span>}
                </div>
              </div>

              <div className="soap-item soap-item--o">
                <div className="soap-header">
                  <span className="soap-badge">O</span>
                  <span className="soap-title">客観的情報 (Objective)</span>
                </div>
                <div className="soap-content">
                  {record.objectiveNote || <span className="no-data">記録なし</span>}
                </div>
              </div>

              <div className="soap-item soap-item--a">
                <div className="soap-header">
                  <span className="soap-badge">A</span>
                  <span className="soap-title">評価 (Assessment)</span>
                </div>
                <div className="soap-content">
                  {record.assessmentNote || <span className="no-data">記録なし</span>}
                </div>
              </div>

              <div className="soap-item soap-item--p">
                <div className="soap-header">
                  <span className="soap-badge">P</span>
                  <span className="soap-title">計画 (Plan)</span>
                </div>
                <div className="soap-content">
                  {record.planNote || <span className="no-data">記録なし</span>}
                </div>
              </div>

              {record.additionalNote && (
                <div className="soap-item">
                  <div className="soap-header">
                    <span className="material-symbols-outlined" style={{fontSize: '20px', color: 'var(--text-secondary)'}}>note_add</span>
                    <span className="soap-title">その他・備考</span>
                  </div>
                  <div className="soap-content">
                    {record.additionalNote}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* メタ情報 */}
          <section className="detail-section detail-section--meta">
            <div className="meta-info">
              <span>作成日時: {new Date(record.createdAt).toLocaleString('ja-JP')}</span>
              <span>更新日時: {new Date(record.updatedAt).toLocaleString('ja-JP')}</span>
            </div>
          </section>
        </div>

        {/* 印刷用フッター */}
        <div className="print-footer print-only">
          <p>印刷日時: {new Date().toLocaleString('ja-JP')}</p>
          <p>FMT JAPAN 治療経過管理システム</p>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          
          .print-only {
            display: block !important;
          }
          
          .aip-main__content {
            padding: 0;
          }
          
          .detail-container {
            box-shadow: none;
            border: 1px solid #ddd;
          }
        }

        @media screen {
          .print-only {
            display: none;
          }
        }

        .print-header {
          text-align: center;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #333;
        }

        .print-header h1 {
          font-size: 24px;
          margin-bottom: 5px;
        }

        .print-footer {
          margin-top: 30px;
          padding-top: 10px;
          border-top: 1px solid #ddd;
          text-align: center;
          font-size: 12px;
          color: #666;
        }

        .detail-container {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .detail-section {
          padding: var(--spacing-lg);
          border-bottom: 1px solid var(--border-color);
        }

        .detail-section:last-child {
          border-bottom: none;
        }

        .detail-section--meta {
          background-color: var(--color-solid-gray25);
          border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
        }

        .detail-section__title {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: var(--spacing-md);
          color: var(--text-primary);
        }

        .detail-section__title .material-symbols-outlined {
          color: var(--color-blue600);
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--spacing-md);
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .detail-item--full {
          grid-column: 1 / -1;
        }

        .detail-item label {
          font-size: 0.75rem;
          color: var(--text-secondary);
          font-weight: 500;
        }

        .detail-item span, .detail-item a {
          font-size: 0.875rem;
        }

        .detail-link {
          color: var(--color-blue600);
          font-weight: 500;
        }

        .detail-link:hover {
          text-decoration: underline;
        }

        .record-type-badge {
          display: inline-block;
          padding: 2px 8px;
          font-size: 0.75rem;
          font-weight: 500;
          border-radius: var(--border-radius-sm);
        }

        .record-type-badge--doctor {
          background-color: var(--color-blue50);
          color: var(--color-blue600);
        }

        .record-type-badge--patient {
          background-color: var(--color-success-light);
          color: var(--color-success);
        }

        .disease-badge {
          display: inline-block;
          padding: 2px 8px;
          background-color: var(--color-solid-gray100);
          border-radius: var(--border-radius-sm);
          font-size: 0.8125rem;
        }

        .doctor-info {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
        }

        .doctor-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background-color: var(--color-blue500);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .soap-container {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-md);
        }

        .soap-item {
          border-radius: var(--border-radius-md);
          overflow: hidden;
        }

        .soap-header {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm) var(--spacing-md);
          font-weight: 500;
        }

        .soap-item--s .soap-header { background-color: #e0f2fe; }
        .soap-item--o .soap-header { background-color: #d1fae5; }
        .soap-item--a .soap-header { background-color: #fef3c7; }
        .soap-item--p .soap-header { background-color: #ede9fe; }

        .soap-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          font-size: 0.75rem;
          font-weight: 700;
          color: white;
        }

        .soap-item--s .soap-badge { background-color: #0891b2; }
        .soap-item--o .soap-badge { background-color: #059669; }
        .soap-item--a .soap-badge { background-color: #d97706; }
        .soap-item--p .soap-badge { background-color: #7c3aed; }

        .soap-title {
          font-size: 0.875rem;
        }

        .soap-content {
          padding: var(--spacing-md);
          background-color: white;
          border: 1px solid var(--border-color);
          border-top: none;
          font-size: 0.875rem;
          line-height: 1.6;
          white-space: pre-wrap;
        }

        .no-data {
          color: var(--text-secondary);
          font-style: italic;
        }

        .meta-info {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .aip-button--danger {
          color: var(--color-alert);
          border-color: var(--color-alert);
        }

        .aip-button--danger:hover {
          background-color: var(--color-alert);
          color: white;
        }

        @media (max-width: 768px) {
          .detail-grid {
            grid-template-columns: 1fr 1fr;
          }

          .meta-info {
            flex-direction: column;
            gap: var(--spacing-xs);
          }
        }
      `}</style>
    </>
  )
}
